<template>
  <div class="video-form-content">
    <div class="flex flex-wrap justify-end">
      <vs-input
        class="w-full name-field"
        label="Video name"
        v-model="localVideo.name"
        @blur="saveVideo(localVideo)"
      />
    </div>
    <div class="w-full mt-10">
      <label class="vs-input--label">Slug</label>
      <vx-input-group
        class="mb-base"
        prependClasses="border border-solid border-grey border-r-0"
      >
        <template slot="prepend">
          <div class="prepend-text">
            <vs-switch
              v-model="autoAlias"
              :checked="autoAlias"
              class="auto-alias-checkbox"
            />
          </div>
        </template>

        <vs-input
          v-model="localVideo.slug"
          :disabled="autoAlias"
          class="auto-alias-field"
          @blur="saveVideo(localVideo)"
        />
      </vx-input-group>
    </div>
    <div class="flex md:mt-10 mt-6" v-if="isEditing && embedded">
      <vs-input
        class="w-full id-field"
        label="UUID"
        :disabled="isEditing"
        v-model="localVideo.id"
      />
    </div>
    <div class="flex md:mt-10 mt-6">
      <vs-input
        class="w-full source-field"
        label="Source Media"
        :disabled="isEditing"
        v-model="localVideo.source"
        @blur="saveVideoIfNew"
      />
    </div>
    <div class="flex flex-wrap md:mt-10 mt-6">
      <vs-input
        class="w-full duration-field"
        label="Duration"
        v-model="duration"
        readonly
      />
    </div>
    <div class="md:mt-10 mt-6">
      <label class="vs-input--label">Instructor</label>
      <v-select
        v-model="localVideo.instructor_id"
        :reduce="(instructor) => instructor.value"
        placeholder="Select an instructor"
        :options="getInstructors"
        @input="saveVideo(localVideo)"
      />
    </div>
    <div class="md:mt-10 mt-6">
      <label class="vs-input--label">Video Type</label>
      <v-select
        class="type-field"
        v-model="localVideo.type"
        :clearable="false"
        :reduce="(type) => type.value"
        placeholder="Select a type"
        :options="videoTypeOptions"
        @input="saveVideo(localVideo)"
      />
    </div>
    <div class="w-full md:mt-10 mt-6" v-if="customContentVideosEnabled">
      <vx-tooltip
        v-if="customContentToggleTooltip"
        :text="customContentToggleTooltip"
        class="custom-content-toggle-tooltip"
      >
        <div class="flex items-center">
          <vs-switch
            v-model="isCustomContentEnabled"
            :checked="isCustomContentEnabled"
            class="mr-5 is-custom-content-field"
            :disabled="isCustomContentToggleDisabled"
          />
          <label>{{ customContentStrings.toggleLabel }}</label>
        </div>
      </vx-tooltip>
      <template v-else>
        <div class="flex items-center">
          <vs-switch
            v-model="isCustomContentEnabled"
            :checked="isCustomContentEnabled"
            class="mr-5 is-custom-content-field"
            :disabled="isCustomContentToggleDisabled"
          />
          <label>{{ customContentStrings.toggleLabel }}</label>
        </div>
      </template>
      <p class="text-sm text-grey mt-2">
        {{ customContentStrings.publicStateHelperText }}
      </p>
    </div>
    <div class="flex flex-wrap md:mt-10 mt-6">
      <vs-input
        class="w-full jw-player-id-field"
        label="JW Player Media ID"
        :disabled="!isJwplayerEnabled"
        v-model="localVideo.jwplayer_id"
        @blur="saveVideo(localVideo)"
      />
      <div class="mt-1 video-preview" v-if="isEditing && embedded">
        <div class="mt-5">
          <i
            ><a
              :href="`https://dashboard.jwplayer.com/#/content/detail?key=${getCurrentVideo.jwplayer_id}`"
              target="_blank"
              >Go to this video in dashboard.jwplayer.com</a
            ></i
          >
        </div>
      </div>
    </div>
    <div class="flex md:mt-10 mt-6" v-if="isEditing">
      <vs-input
        class="w-full status-field"
        label="Status"
        v-model="localVideo.status"
      />
    </div>
    <div class="flex flex-wrap md:mt-10 mt-6">
      <Skills
        :content-type="CONTENT_TYPES.VIDEO"
        :skills-list="videoSkills"
        :editMode="isEditing"
        @add:skillsList="handleVideoSkillsAdded"
        @delete:skillsList="handleVideoSkillsDeleted"

      />
    </div>
    <div class="flex flex-wrap mt-10">
      <vs-alert
        color="warning"
        icon-pack="feather"
        icon="icon-info"
        class="w-full"
        v-if="rememberToSaveFiles"
      >
        Please remember to save the video after add or delete a file.
      </vs-alert>
      <FilesAttachments
        type="video"
        :files="getCurrentVideoFiles"
        :loading="filesLoading"
        :content="getCurrentVideo"
        @file-deleted="deleteFileFromVideo"
        @file-added="addFileToVideo"
        @file-changed="fileChanged"
      />
    </div>
    <div class="flex md:mt-10 mt-6 -mx-2 action-buttons" v-if="embedded">
      <vs-button
        class="w-full mx-2 save-button"
        color="primary"
        icon-pack="feather"
        @click.stop="saveAndClick"
        >Save</vs-button
      >
    </div>
  </div>
</template>
<script>
import slug from 'slug-generator'
import cloneDeep from 'lodash.clonedeep'
import { mapGetters, mapMutations, mapActions } from 'vuex'
import { mask } from 'vue-the-mask'
import 'vue2-timepicker/dist/VueTimepicker.css'
import { HmsToSeconds, secondsToHms } from '@/utils/utils.js'
import Skills from '@/components/Skills'
import CONSTANTS from '@/constants'
import {
  CUSTOM_CONTENT_AUDIENCE_TYPES,
  CUSTOM_CONTENT_STRINGS,
} from '@/constants/customContent'
import {
  decorateLatestDialog,
  normalizeCustomContentAudienceType,
  shouldConfirmCustomToPublicTransition,
  shouldDisableCustomContentToggle,
} from '@/services/customContentToggle.service'
import vSelect from 'vue-select'
import FilesAttachments from '@/components/FilesEngineAttachments'

export default {
  components: {
    'v-select': vSelect,
    Skills,
    FilesAttachments,
  },
  directives: {
    mask,
  },
  props: {
    embedded: Boolean,
    onClick: {
      type: Function,
      required: true,
    },
  },
  data() {
    return {
      CONTENT_TYPES: CONSTANTS.CONTENT_TYPES,
      customContentStrings: CUSTOM_CONTENT_STRINGS,
      autoAlias: true,
      localVideo: {},
      duration: '00:00:00',
      durationInSeconds: '0',
      rememberToSaveFiles: false,
      videoTypeOptions: [
        { value: 'lesson', label: 'Lesson' },
        { value: 'lab_explanation', label: 'Lab Explanation' },
        { value: 'lab_solution', label: 'Lab Solution' },
        { value: 'ine_live', label: 'INE Live' },
      ],
      filesLoading: false,
      persistedCustomContentAudienceType: CUSTOM_CONTENT_AUDIENCE_TYPES.CUSTOM,
      persistedAudienceSourceId: null,
    }
  },
  watch: {
    customContentVideosEnabled: function (newValue) {
      if (!newValue) {
        this.localVideo = {
          ...this.localVideo,
          audience_type: CUSTOM_CONTENT_AUDIENCE_TYPES.PUBLIC,
        }
      }
    },
    'localVideo.name': function (newValue) {
      if (this.autoAlias) {
        this.localVideo.slug = slug(newValue)
      }
    },
    autoAlias: function (newValue) {
      if (newValue) {
        this.localVideo.slug = slug(this.localVideo.name)
      }
    },
    getCurrentVideo: function (newValue) {
      this.localVideo = cloneDeep(newValue)
      this.durationInSeconds = this.localVideo.duration_in_seconds
      this.duration = secondsToHms(this.durationInSeconds)
      this.setPersistedCustomContentAudienceType(newValue)
    },
    duration: function (newValue) {
      this.durationInSeconds = HmsToSeconds(newValue)
      this.transformAndSaveVideo()
    },
    'localVideo.files': async function () {
      if (
        JSON.stringify(this.localVideo.files) ===
        JSON.stringify(this.getCurrentVideo.files)
      ) {
        this.rememberToSaveFiles = false
      }
      if (this.localVideo.files && this.localVideo.files.length) {
        this.filesLoading = true
        await this.getAllVideoFiles(this.localVideo.files)
        this.filesLoading = false
      }
      if (this.localVideo.files && !this.localVideo.files.length) {
        this.resetFilesInVideo()
      }
    },
    rememberToSaveFiles: function (newValue) {
      this.$emit('prevent-close', newValue)
    },
  },
  computed: {
    ...mapGetters('videos', ['getCurrentVideo', 'getCurrentVideoFiles']),
    ...mapGetters('featureFlags', ['customContentVideosEnabled']),
    ...mapGetters('instructors', ['getInstructorsEntries', 'getInstructors']),
    ...mapGetters('taxonomy', {
      getVideoTaxonomy: 'getVideoTaxonomy',
     }),
    isEditing() {
      return !!this.getCurrentVideo.id
    },
    currentSavedAudienceType() {
      return normalizeCustomContentAudienceType(
        this.persistedCustomContentAudienceType
      )
    },
    isCustomContentToggleDisabled() {
      return shouldDisableCustomContentToggle({
        featureEnabled: this.customContentVideosEnabled,
        isEditing: this.isEditing,
        persistedAudienceType: this.currentSavedAudienceType,
      })
    },
    customContentToggleTooltip() {
      if (!this.isCustomContentToggleDisabled) return null
      return this.customContentStrings.publicToCustomTooltip
    },
    isCustomContentEnabled: {
      get() {
        return (
          this.localVideo.audience_type === CUSTOM_CONTENT_AUDIENCE_TYPES.CUSTOM
        )
      },
      set(value) {
        if (value === this.isCustomContentEnabled) return
        if (value && this.isCustomContentToggleDisabled) return

        if (
          shouldConfirmCustomToPublicTransition({
            featureEnabled: this.customContentVideosEnabled,
            isEditing: this.isEditing,
            persistedAudienceType: this.currentSavedAudienceType,
            nextIsCustomContentEnabled: value,
          })
        ) {
          this.openConvertToPublicConfirmationDialog(
            () => {
              this.applyCustomContentToggle(value)
            },
            () => {
              this.localVideo = {
                ...this.localVideo,
                audience_type: CUSTOM_CONTENT_AUDIENCE_TYPES.CUSTOM,
              }
            }
          )
          return
        }

        this.applyCustomContentToggle(value)
      },
    },
    isJwplayerEnabled() {
      return this.localVideo.type == 'ine_live'
    },
    videoSkills() {
      const currentSkills = Array.isArray(this.getCurrentVideo.taxonomy_skills) ? this.getCurrentVideo.taxonomy_skills : []
      const addedSkills = this.getVideoTaxonomy && this.getVideoTaxonomy.contentId === this.getCurrentVideo.id && Array.isArray(this.getVideoTaxonomy.addedSkills) ? this.getVideoTaxonomy.addedSkills : []
      return [...currentSkills, ...addedSkills]
    }
  },
  methods: {
    ...mapMutations('videos', {
      saveVideo: 'STORE_CURRENT_VIDEO',
      changeFileInVideo: 'CHANGE_FILE_IN_VIDEO',
      resetFilesInVideo: 'RESET_FILES_IN_VIDEO',
    }),
    ...mapMutations('taxonomy', {
      storeVideoTaxonomy: 'STORE_VIDEO_TAXONOMY',
      deleteVideoTaxonomy: 'DELETE_VIDEO_TAXONOMY_SKILL',
      clearVideoTaxonomy: 'CLEAR_VIDEO_TAXONOMY',
    }),
    ...mapActions('instructors', ['getAllInstructors']),
    ...mapActions('videos', ['getAllVideoFiles']),
    setPersistedCustomContentAudienceType(video = this.getCurrentVideo) {
      const normalizedAudienceType = normalizeCustomContentAudienceType(
        video && video.audience_type
      )

      if (!this.isEditing) {
        this.persistedCustomContentAudienceType = normalizedAudienceType
        this.persistedAudienceSourceId = null
        return
      }

      const sourceId = video && video.id
      if (!sourceId) return
      if (this.persistedAudienceSourceId === sourceId) return

      this.persistedAudienceSourceId = sourceId
      this.persistedCustomContentAudienceType = normalizedAudienceType
    },
    syncPersistedCustomContentAudienceType(audienceType) {
      this.persistedCustomContentAudienceType = normalizeCustomContentAudienceType(
        audienceType
      )
      this.persistedAudienceSourceId = this.localVideo.id || null
    },
    applyCustomContentToggle(value) {
      this.localVideo = {
        ...this.localVideo,
        audience_type: value
          ? CUSTOM_CONTENT_AUDIENCE_TYPES.CUSTOM
          : CUSTOM_CONTENT_AUDIENCE_TYPES.PUBLIC,
      }
      this.saveVideo(this.localVideo)
    },
    openConvertToPublicConfirmationDialog(onAccept, onCancel = () => {}) {
      this.$vs.dialog({
        type: 'confirm',
        color: 'primary',
        title: this.customContentStrings.convertToPublicTitle,
        text: this.customContentStrings.convertToPublicBody.trim(),
        acceptText: this.customContentStrings.convertToPublicAcceptText,
        cancelText: this.customContentStrings.convertToPublicCancelText,
        accept: onAccept,
        cancel: onCancel,
      })
      this.$nextTick(() => {
        decorateLatestDialog('custom-content-convert-dialog', {
          maxWidth: '500px',
          normalizeText: true,
        })
      })
    },
    syncLocalVideoToStore() {
      this.saveVideo(this.localVideo)
    },
    saveAndClick() {
      this.syncLocalVideoToStore()
      this.onClick()
      this.rememberToSaveFiles = false
    },
    markFilesAsSaved() {
      this.rememberToSaveFiles = false
    },
    resetLocalVideo(video = this.getCurrentVideo) {
      this.localVideo = cloneDeep(video || this.getCurrentVideo)
      this.durationInSeconds = this.localVideo.duration_in_seconds
      this.duration = secondsToHms(this.durationInSeconds)
      this.setPersistedCustomContentAudienceType(this.localVideo)
      this.rememberToSaveFiles = false
    },
    saveVideoIfNew() {
      if (!this.isEditing) {
        this.saveVideo(this.localVideo)
      }
    },
    transformAndSaveVideo() {
      this.localVideo.duration_in_seconds = this.durationInSeconds
      this.saveVideo(this.localVideo)
    },
    deleteFileFromVideo(file) {
      // this.deleteFile(file)
      const index = this.localVideo.files.indexOf(file.id)
      if (index > -1) {
        this.localVideo.files.splice(index, 1)
      }
      this.rememberToSaveFiles = true
    },
    addFileToVideo(file) {
      // this.addFile(file)
      this.localVideo.files = [...this.localVideo.files, file.id]
      this.rememberToSaveFiles = true
    },
    fileChanged(payload) {
      this.changeFileInVideo(payload)
    },
    handleVideoSkillsAdded(newSkillsList) {
      const skillsToAdd = newSkillsList.filter(ns => !this.videoSkills.find(s => s.skillId === ns.skillId))
      this.storeVideoTaxonomy({
        contentId: this.getCurrentVideo.id,
        addedSkills: skillsToAdd
      })
    },
    handleVideoSkillsDeleted(deletedSkill) {
      this.deleteVideoTaxonomy({
        contentId: this.getCurrentVideo.id,
        skillId: deletedSkill.skillId,
      })
    },
  },
  async mounted() {
    this.localVideo = cloneDeep(this.getCurrentVideo)
    this.durationInSeconds = this.localVideo.duration_in_seconds
    this.duration = secondsToHms(this.durationInSeconds)
    this.setPersistedCustomContentAudienceType(this.getCurrentVideo)
    if (!this.embedded && !Object.keys(this.getInstructorsEntries).length) {
      await this.getAllInstructors()
    }
    this.clearVideoTaxonomy({contentId: this.getCurrentVideo.id})
  },
}
</script>
